/**
 * Session WebSocket Hook
 *
 * App-agnostic wrapper around @selna/session's useWebSocketSession.
 * Reads connection config (API URL, auth, tracking) from SessionServicesContext.
 * Each app provides its own auth and tracking implementations via the context.
 *
 * Usage:
 * ```tsx
 * const session = useSessionWebSocket({
 *   sessionTrackingId,
 *   autoConnect: false,
 * });
 * ```
 */

import { useMemo } from 'react';
import {
  useWebSocketSession as useBaseWebSocketSession,
  type WebSocketSessionConfig,
  type UseWebSocketSessionReturn,
} from '@selna/session';
import { useSessionServices } from '../context/SessionServicesContext';

// ============================================================================
// Config Interface (session-specific, without app-specific auth/tracking)
// ============================================================================

/**
 * Session-specific configuration for WebSocket session.
 * App-specific config (auth, tracking, API URL) comes from SessionServicesContext.
 */
export interface SessionWebSocketConfig extends Omit<
  WebSocketSessionConfig,
  'credentials' | 'createAuthHeaders' | 'sessionTracking' | 'apiBaseUrl'
> {
  /**
   * Optional API base URL override.
   * Defaults to the one from SessionServicesContext.
   */
  apiBaseUrl?: string;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * App-agnostic WebSocket session hook.
 * Uses SessionServicesContext for auth and tracking config.
 */
export function useSessionWebSocket(
  config: SessionWebSocketConfig = {}
): UseWebSocketSessionReturn {
  const services = useSessionServices();
  const { webSocketConfig } = services;

  // Create memoized config that merges session-specific with context-provided config
  const packageConfig = useMemo<WebSocketSessionConfig>(() => {
    return {
      ...config,
      apiBaseUrl: config.apiBaseUrl ?? webSocketConfig.apiBaseUrl,
      credentials: webSocketConfig.credentials,
      createAuthHeaders: webSocketConfig.createAuthHeaders,
      sessionTracking: webSocketConfig.createSessionTracking(config.sessionTrackingId),
    };
  }, [
    webSocketConfig,
    config.sessionTrackingId,
    config.apiBaseUrl,
    config.systemInstructions,
    config.voice,
    config.model,
    config.enableVAD,
    config.vadThreshold,
    config.temperature,
    config.characterRole,
    config.autoConnect,
    config.autoStartRecording,
    config.retryOnError,
    config.maxRetryAttempts,
    config.retryBaseDelayMs,
  ]);

  return useBaseWebSocketSession(packageConfig);
}

// Default export for convenience
export default useSessionWebSocket;

// Re-export types from @selna/session for convenience
export type {
  WebSocketSessionState,
  UseWebSocketSessionReturn,
  TranscriptMessage,
  AudioQualityStats,
  ConnectOptions,
  AnalysisModeCallbacks,
} from '@selna/session';
